// 🎬 «쇼릴형» 무대 — 어두운 바탕 위 원색 판 · 장면이 바뀔 때 원색 띠가 쓸고 지나가는 컷 · 제목 «쾅» · 격자선 · 3D 구슬 층.
//   모션 무대 가족(`motion-stage-family.ts`)의 한 무대 — 장표 층(`Overlay169SlideLayer`)이 무대 맵(`motion-stages.tsx`)에서 꺼내 끼운다.
//   ★글자 자리 계산은 모션 브리핑과 같은 함수(`briefStageFit`)★ — «글자 ⊂ 칸 ⊂ 판 · 한 줄 라벨 · 자막 띠 비켜서기» 불변식과
//   경계 검사 표식(`data-brief-*`)을 그대로 물려받는다. 다른 것은 «그리는 모양»뿐이다(변신 없음 · 판 색 · 전환 · 바탕).
//   순수 렌더: 같은 (프레임·장면·앞 장면) = 같은 그림. 난수는 시드 고정 · 시계·프레임 간 상태 없음.
//   3D 층은 이미 구운 16:9 자산만 쓴다(`<Motion3dLayer>`) — 못 받으면 그 층만 비고 2D 무대는 그대로다(검은 프레임 없음).
import React, { useLayoutEffect, useRef } from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import { DURATION_MS, EASE_ENTER_SOFT, lin, win } from '../../lib/motion-kit/spring';
import { Motion3dLayer } from '../../lib/motion-kit/3d/Motion3dLayer';
import { BRIEF_CONTENT_FADE_SEC, BRIEF_CONTENT_TAIL_SEC, BRIEF_PAD, briefGlueLastWords } from './motion-brief-shape';
import { briefStageFit } from './MotionBriefStage';
import type { MotionStageExitProps, MotionStageProps } from './motion-stages';
import { SlideStageInsetContext } from './stage-inset';
import { useBalancedTitle } from './title-balance';
import { KeywordText, KitWords, type KeywordEmphasisId } from './motion';
import { FW, KO_TEXT, LINE_HEIGHT } from './tokens';
import {
  REEL_BAND_TRAIL_W, REEL_BG, REEL_GRID, REEL_GRID_PX, REEL_ORB_TYPES, REEL_RADIUS_MAX, REEL_WIPE_SEC,
  reelAccentOnDark, reelFillColor, reelFillOf, reelSlamCap, reelSlamScale, reelWipe,
} from './showreel-tokens';

/**
 * 바탕 결 — 얇은 격자선 + 가장자리 어둠. 캔버스 하나에 그린다(브라우저 내보내기는 CSS 그라데이션·배경 타일을 못 칠한다).
 *   ★필름 입자는 뺐다(10-02)★ — 움직이는 입자는 mp4 를 모션 브리핑의 7~13배로, 고정 무늬도 1.7~2.9배로 키웠다
 *   (본부 기준 1.5배 · 받기·올리기 대기 = 유저 마찰). 질감은 격자선·가장자리 어둠이 맡는다.
 */
const ReelBackdrop: React.FC = () => {
  const { width: W, height: H } = useVideoConfig();
  const ref = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    const k = W / 1920;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, W, H);
    // 격자선
    ctx.fillStyle = REEL_GRID;
    const step = REEL_GRID_PX * k;
    for (let x = step; x < W; x += step) ctx.fillRect(Math.round(x), 0, Math.max(1, Math.round(k)), H);
    for (let y = step; y < H; y += step) ctx.fillRect(0, Math.round(y), W, Math.max(1, Math.round(k)));
    // 가장자리 어둠
    const g = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.35, W / 2, H / 2, Math.hypot(W, H) * 0.6);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, 'rgba(0,0,0,0.42)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }, [W, H]);
  return <canvas ref={ref} data-reel-backdrop="" width={W} height={H} style={{ position: 'absolute', left: 0, top: 0, width: W, height: H, pointerEvents: 'none' }} />;
};

/** 판 하나 — 짙은 판은 얇은 원색 테두리 + 왼쪽 원색 막대, 원색 판은 통색. */
const ReelPanel: React.FC<{ rect: { x: number; y: number; w: number; h: number; r: number }; type: string; brand: string; k: number; mark?: boolean; opacity?: number; scale?: number }> = ({ rect, type, brand, k, mark = false, opacity = 1, scale = 1 }) => {
  const fill = reelFillOf(type);
  const full = rect.w >= 1920 * k - 1 && rect.r === 0; // 포스터(전체 화면)
  const r = full ? 0 : Math.min(rect.r, Math.round(REEL_RADIUS_MAX * k));
  const line = reelAccentOnDark(brand);
  return (
    <div
      {...(mark ? { 'data-brief-shape': type } : {})}
      style={{
        position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h, borderRadius: r, boxSizing: 'border-box',
        background: full ? 'transparent' : reelFillColor(fill, brand), opacity,
        ...(fill === 'panel' && !full ? { border: `${Math.max(1, Math.round(2 * k))}px solid ${line}` } : {}),
        ...(scale !== 1 ? { transform: `scale(${scale.toFixed(4)})` } : {}),
      }}
    >
      {fill === 'panel' && !full ? (
        <div style={{ position: 'absolute', left: 0, top: Math.round(18 * k), bottom: Math.round(18 * k), width: Math.round(8 * k), background: line }} />
      ) : null}
    </div>
  );
};

export const ShowreelStage: React.FC<MotionStageProps> = ({ f, slide, prevSlide, sceneFrames, brand, reserveRightPx = 0, captionTopPx, captionBottomPx, captionWidthPx, prevCaption, titleColors, opening = false, children }) => {
  const { width: W, height: H, fps } = useVideoConfig();
  const frame = useCurrentFrame();
  const caption = typeof captionTopPx === 'number' && typeof captionBottomPx === 'number' ? { top: captionTopPx, bottom: captionBottomPx, width: captionWidthPx } : undefined;
  const split = titleColors !== undefined;
  const { k, core, cin, avail, cur } = briefStageFit(slide, W, H, reserveRightPx, caption, split);
  const to = cur.rect;
  const t = Math.max(0, f) / fps;
  // 앞 장면도 장표면 «띠 쓸림 컷» — 띠 뒤에서 이번 장면이 드러난다. 아니면(첫 장면·영상 뒤) 바탕이 전환 길이로 켜지고 판이 살짝 커지며 선다.
  const wipe = prevSlide ? reelWipe(t, W) : null;
  const prevRect = prevSlide ? briefStageFit(prevSlide, W, H, reserveRightPx, prevCaption ?? caption, split).cur.shape : null;
  const canvasIn = prevSlide || opening ? 1 : EASE_ENTER_SOFT(lin(t, 0, DURATION_MS.transition / 1000));
  // 첫 장면은 판이 0프레임부터 서 있다(첫 프레임이 빈 바탕이면 «검은 프레임»으로 잡힌다) — 크기만 살짝 자란다.
  const grow = prevSlide ? 1 : EASE_ENTER_SOFT(lin(t, 0, 0.3));
  const settle = prevSlide || opening ? 1 : grow;
  // 글자는 띠가 다 지나간 뒤 들어온다 — 쓸림 중에는 판만 창 안에 있다(★브라우저 내보내기는 창과 겹치지 않는 자식을 잘라 내지 않고 통째로 그렸다
  //   → 쓸림 첫 2~3프레임에 새 판이 전부 보였다 · 10-02 mp4 실측 급변 10회★).
  const lead = prevSlide ? REEL_WIPE_SEC : 0.3;
  const endSec = typeof sceneFrames === 'number' && sceneFrames > 0 ? sceneFrames / fps - BRIEF_CONTENT_TAIL_SEC : undefined;
  const alpha = win(t, lead, endSec !== undefined && endSec > 0.5 ? endSec : undefined, BRIEF_CONTENT_FADE_SEC);
  const poster = slide.type === 'posterStack';
  const inset = poster
    ? { ...core, bottom: Math.max(core.bottom, avail.bottom) + Math.round(24 * k) }
    : { top: to.y + BRIEF_PAD.y * k, left: to.x + BRIEF_PAD.x * k, right: W - (to.x + to.w) + BRIEF_PAD.x * k, bottom: H - (to.y + to.h) + cur.padBottom, ...(cin.band ? { hideTitle: true } : {}), ...(cur.compact ? { compact: true } : {}), ...(cur.subCols ? { subCols: true } : {}), ...(cur.cells ? { cells: cur.cells } : {}) };
  const band = cin.band;
  const titleAt = Math.round(lead * fps);
  const content = (slide.content ?? {}) as { keyword?: string; keywordEmphasis?: KeywordEmphasisId };
  const bandGlued = band ? briefGlueLastWords(band.text, band.fontPx, band.width) : '';
  // 📐 #6394 붙여도 마지막 줄이 한 어절이면 띠 폭·실제 글꼴로 재서 고르게 다시 나눈다(`title-balance.ts` · 고른 제목은 그대로).
  const [bandRef, bandText] = useBalancedTitle(bandGlued, { avail: 'self', keyword: content.keyword });
  const slam = band ? reelSlamScale(t - lead, reelSlamCap(band, W, H)) : 1;
  const titleInk = titleColors ? { ...titleColors, textMain: '#FFFFFF', accent: reelAccentOnDark(brand, REEL_BG), isDark: true } : undefined;
  // 3D 구슬 샌드위치 — 첫 장면·큰 숫자에만(렌더 시간 예산 +30% · 10-02 실측 운영 표본 +36% → 제목 장면 전부에서 줄임). 재생 시작 = 이 장면의 시작 프레임.
  const orbs = REEL_ORB_TYPES.has(slide.type) && (opening || slide.type === 'bigNumber');
  const sceneStart = frame - Math.max(0, Math.round(f));
  const orbAt = sceneStart + (prevSlide ? Math.round(REEL_WIPE_SEC * 0.5 * fps) : 0);

  const scene = (
    <>
      {orbs ? <Motion3dLayer assetId="orbs-back-169" at={orbAt} color={reelAccentOnDark(brand, REEL_BG)} opacity={0.9} /> : null}
      {!wipe || wipe.edge > cur.shape.x + 1 ? <ReelPanel rect={cur.shape} type={slide.type} brand={brand} k={k} mark opacity={settle} scale={0.96 + 0.04 * grow} /> : null}
      {band && titleInk ? (
        <div ref={bandRef} data-brief-title="" style={{ position: 'absolute', left: band.left, top: band.top, width: band.width, textAlign: 'center', opacity: alpha,
          fontSize: band.fontPx, fontWeight: FW.extrabold, lineHeight: LINE_HEIGHT.tight, color: titleInk.textMain, ...KO_TEXT, textWrap: 'balance',
          ...(slam !== 1 ? { transform: `scale(${slam.toFixed(4)})` } : {}) }}>
          <KitWords kit fps={fps} f={f} at={titleAt} text={bandText}>
            <KeywordText text={bandText} keyword={content.keyword && !bandText.includes(content.keyword) ? content.keyword.replace(/ /g, ' ') : content.keyword} emphasis={content.keywordEmphasis} colors={titleInk} f={f - titleAt - 12} />
          </KitWords>
        </div>
      ) : null}
      <SlideStageInsetContext.Provider value={inset}>
        <AbsoluteFill data-brief-content="" style={{ opacity: alpha, ...(cur.contentScale < 1
          ? { transform: `scale(${cur.contentScale.toFixed(4)})`, transformOrigin: `${Math.round(to.x + to.w / 2)}px ${Math.round(to.y + to.h / 2)}px` }
          : {}) }}>{children}</AbsoluteFill>
      </SlideStageInsetContext.Provider>
      {orbs ? <Motion3dLayer assetId="orbs-front-169" at={orbAt} color={reelAccentOnDark(brand, REEL_BG)} /> : null}
    </>
  );

  // 영상·그림 장면 뒤에 들어올 때 — 조리개 마스크(1초)가 바탕색 원을 가운데서부터 연다. 자산을 못 받으면 아래 페이드만 남는다(2D 대체).
  const fromVideo = !prevSlide && !opening;
  return (
    <AbsoluteFill>
      {fromVideo ? <Motion3dLayer assetId="iris-mask-169" at={sceneStart} color={REEL_BG} /> : null}
    <AbsoluteFill style={{ background: REEL_BG, opacity: canvasIn }}>
      <ReelBackdrop />
      {wipe ? (
        <>
          {/* 아직 안 드러난 쪽(띠 오른쪽) — 앞 장면의 판만 남아 있다(글자는 0.1초 전에 이미 빠졌다). */}
          {prevSlide && prevRect && wipe.edge + wipe.bandW < prevRect.x + prevRect.w - 1 ? (
            <div style={{ position: 'absolute', left: Math.max(0, wipe.edge + wipe.bandW), top: 0, width: Math.max(0, W - Math.max(0, wipe.edge + wipe.bandW)), height: H, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', left: -Math.max(0, wipe.edge + wipe.bandW), top: 0, width: W, height: H }}>
                <ReelPanel rect={prevRect} type={prevSlide.type} brand={brand} k={k} />
              </div>
            </div>
          ) : null}
          {/* 드러난 쪽(띠 왼쪽) — 창이 넓어지며 이번 장면이 보인다. */}
          <div style={{ position: 'absolute', left: 0, top: 0, width: Math.max(0, Math.min(W, wipe.edge)), height: H, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, width: W, height: H }}>{scene}</div>
          </div>
          {/* 원색 띠 + 뒤따르는 얇은 흰 선 */}
          <div style={{ position: 'absolute', left: wipe.edge, top: 0, width: wipe.bandW, height: H, background: brand }} />
          <div style={{ position: 'absolute', left: wipe.edge - Math.round(W * REEL_BAND_TRAIL_W) - Math.round(10 * k), top: 0, width: Math.max(1, Math.round(W * REEL_BAND_TRAIL_W)), height: H, background: 'rgba(255,255,255,0.9)' }} />
        </>
      ) : scene}
    </AbsoluteFill>
    </AbsoluteFill>
  );
};

/** 나가는 무대 — 장표 장면 다음이 영상·그림 장면이면 바탕과 판이 전환 길이(800ms)로 빠진다(하드컷이면 평균 밝기가 한 프레임에 튄다). */
export const ShowreelExit: React.FC<MotionStageExitProps> = ({ f, prevSlide, brand, reserveRightPx = 0, prevCaption, split = false }) => {
  const { width: W, height: H, fps } = useVideoConfig();
  const frame = useCurrentFrame();
  const t = Math.max(0, f) / fps;
  const out = EASE_ENTER_SOFT(lin(t, 0, DURATION_MS.transition / 1000));
  if (out >= 1) return null;
  const { k, cur } = briefStageFit(prevSlide, W, H, reserveRightPx, prevCaption, split);
  return (
    <AbsoluteFill>
      <AbsoluteFill style={{ background: REEL_BG, opacity: 1 - out }}>
        <ReelPanel rect={cur.shape} type={prevSlide.type} brand={brand} k={k} />
      </AbsoluteFill>
      {/* 조리개가 가운데서부터 다음 장면을 연다(원 밖 = 바탕색). 자산을 못 받으면 위 페이드만 남는다. */}
      <Motion3dLayer assetId="iris-mask-169" at={frame - Math.max(0, Math.round(f))} color={REEL_BG} invert opacity={1 - out} />
    </AbsoluteFill>
  );
};
